'use client';

import { useIsomorphicLayoutEffect } from './use-isomorphic-layout-effect';

let locks = 0;
let unlock = () => {};

function lockBody(): () => void {
  // Measure before changing body styles; nested locks reuse the first lock.
  const html = document.documentElement;
  const scrollbarWidth = Math.max(0, window.innerWidth - html.clientWidth);
  let paddingProperty = 'padding-right';
  let padding = 0;
  if (scrollbarWidth) {
    const scrollbarLeft = Math.round(html.getBoundingClientRect().left) + html.scrollLeft;
    paddingProperty = scrollbarLeft ? 'padding-left' : 'padding-right';
    padding =
      Number.parseFloat(getComputedStyle(document.body).getPropertyValue(paddingProperty)) || 0;
  }
  const style = document.body.style;
  const ios =
    /iP(hone|ad|od)|iOS/.test(navigator.platform) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  // Only iOS needs scroll offsets. Read them before changing any body styles.
  const scrollX = ios ? window.scrollX : 0;
  const scrollY = ios ? window.scrollY : 0;
  const offsetLeft = ios ? Math.floor(window.visualViewport?.offsetLeft || 0) : 0;
  const offsetTop = ios ? Math.floor(window.visualViewport?.offsetTop || 0) : 0;
  const properties = [
    'overflow',
    'overflow-x',
    'overflow-y',
    ...(scrollbarWidth ? [paddingProperty] : []),
    ...(ios ? ['position', 'top', 'left', 'right'] : []),
  ];
  const previous = properties.map((property) => ({
    property,
    value: style.getPropertyValue(property),
    priority: style.getPropertyPriority(property),
  }));

  style.setProperty('overflow', 'hidden');
  if (scrollbarWidth) {
    style.setProperty(paddingProperty, `${padding + scrollbarWidth}px`);
  }
  if (ios) {
    Object.assign(style, {
      position: 'fixed',
      top: `${-(scrollY - offsetTop)}px`,
      left: `${-(scrollX - offsetLeft)}px`,
      right: '0',
    });
  }

  return () => {
    for (const { property, value, priority } of previous) {
      style.setProperty(property, value, priority);
    }
    if (ios) window.scrollTo(scrollX, scrollY);
  };
}

/** Lock body scrolling for the backdrop's lifetime, including its exit transition. */
export function useBodyScrollLock(enabled = false): void {
  useIsomorphicLayoutEffect(() => {
    if (!enabled) return;
    locks++;
    if (locks === 1) unlock = lockBody();
    return () => {
      locks--;
      if (!locks) unlock();
    };
  }, [enabled]);
}
